// DISP-6 — the BL (bon de livraison). Pure render of a model from dispatchSlipModel.mjs: it fetches nothing and
// posts nothing, and prints through DocumentSheet (any print path that swaps DocumentSheet's renderer keeps working).
// Quantity-only by default; `showPrices` adds unit price + line amount (Settings > Formatting rate_decimals / amount_decimals).
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatMoneyPrecise, formatQty, formatDate } from '../../utils/format';
import DocumentSheet from './DocumentSheet';
import { useDocTemplate, showBrand, titleStyle, tplColumns, tplSection, tplVars, cells, TplPart, TplEnd, tplShown } from './docTemplate';

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// One tile per A4 page height, so a long draft/cancelled slip carries the mark on every printed page.
const markUrl = (word) => `url("data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='210' height='297' viewBox='0 0 210 297'><text x='105' y='85' text-anchor='middle' transform='rotate(-35 105 85)' font-family='Arial,sans-serif' font-weight='800' font-size='24' fill='rgba(177,74,48,.16)'>${esc(word)}</text></svg>`)}")`;

const Sign = ({ title, name, tr }) => (
  <div className="doc-bl-sign">
    <div className="doc-bl-sign-t">{title}</div>
    <div className="doc-bl-sign-l">{tr('sign.name')}: {name || ''}</div>
    <div className="doc-bl-sign-l">{tr('sign.date')}:</div>
    <div className="doc-bl-sign-l doc-bl-sign-big">{tr('sign.signature')}:</div>
  </div>
);

export default function DispatchSlipDocument({ model: m, onClose, docId }) {
  const { t: tr } = useTranslation('dispatchSlip');
  const copy = m?.kind === 'copy';
  const money = (v) => (v == null ? '—' : formatMoney(v));
  // v0.46 785: the admin document template; seeded default = this exact slip. Prices / received print only when the slip asks for them AND the
  // template allows them (a column the template hides never prints); the signature block likewise.
  const tpl = useDocTemplate('bl', { document_number: m?.number || '', date: m?.date ? formatDate(m.date) : '' });
  const rec = !!m && (m.showReceived || m.writeIn);
  const vis = tplColumns(tpl, ['item', 'qty', 'unit', ...(m?.showPrices ? ['unit_price', 'amount'] : []), ...(rec ? ['received'] : [])]);
  const cols = vis.length;

  return (
    <DocumentSheet onClose={onClose} doc={{ type: 'bl', id: docId }} paper={tpl?.page} frame={tpl?.frame} vars={tplVars(tpl, { no: m?.number, date: m?.date })}>
      {!m ? <p style={{ color: '#6a786e' }}>{tr('loading')}</p> : (
        <div className="doc-bl" data-state={m.state} data-kind={m.kind}>
          {m.watermark && <div className="doc-bl-wm" aria-hidden="true" style={{ backgroundImage: markUrl(tr(`watermark.${m.watermark}`)) }} />}
          <TplPart tpl={tpl} part="header" />
          <div className="doc-head">
            {showBrand(tpl) && <div className="doc-brand">Gestion Avicole<span className="doc-sub">{tr('brandSub')}</span></div>}
            <div className="doc-title" style={titleStyle(tpl)}>
              <div className="doc-kind">{tr(copy ? 'kind.copy' : 'kind.slip')}</div>
              <div className="doc-ref doc-bl-no">{m.number || tr(copy ? 'ref.copy' : 'ref.draft')}</div>
              {!copy && m.state !== 'draft' && <div className="doc-ref">{tr(`status.${m.state}`)}</div>}
            </div>
          </div>

          {m.watermark && <p className={`doc-bl-banner ${m.watermark}`}><b>{m.watermark === 'void' ? tr('note.void', { date: m.voidAt ? formatDate(m.voidAt) : '' }) : tr(`note.${m.watermark}`)}</b>{m.cancelReason ? ` ${tr('note.cancelReason', { reason: m.cancelReason })}` : ''}</p>}

          <div className="doc-meta">
            <div className="doc-block">
              <div className="doc-label">{tr('customer')}</div>
              <b>{m.customer || '—'}</b>
              {m.pickup
                ? <><div className="doc-label" style={{ marginTop: 10 }}>{tr('fulfilment')}</div><div className="doc-bl-addr"><b>{tr('pickup')}</b></div>
                    <div className="doc-label" style={{ marginTop: 10 }}>{tr('pickupPlace')}</div><div className="doc-bl-addr">{m.pickupPlace || '—'}</div></>
                : <><div className="doc-label" style={{ marginTop: 10 }}>{tr('deliverTo')}</div>
                    {m.address.text
                      ? <div className="doc-bl-addr">{m.address.text}</div>
                      : <div className="doc-bl-addr empty">{tr('noAddress')}</div>}</>}
            </div>
            <div className="doc-block doc-bl-details">
              <div className="doc-label">{tr(copy ? 'detailsCopy' : 'details')}</div>
              {m.date && <div>{tr(copy ? 'deliveryDate' : 'dispatchDate')}: <b>{formatDate(m.date)}</b></div>}
              {m.orderRef && tplShown(tpl, 'order_reference') && <div>{tr('orderRef')}: <b className="doc-bl-mono">{m.orderRef}</b></div>}
              {m.eta && tplShown(tpl, 'target_delivery') && <div>{tr(m.pickup ? 'etaPickup' : 'eta')}: <b>{formatDate(m.eta)}</b></div>}
              {m.from && tplShown(tpl, 'dispatched_from') && <div>{tr('from')}: <b>{m.from}</b></div>}
              {m.vehicle && tplShown(tpl, 'vehicle') && <div>{tr(m.pickup ? 'plate' : 'vehicle')}: <b>{m.vehicle}</b></div>}
              {m.driver && tplShown(tpl, 'driver') && <div>{tr(m.pickup ? 'collector' : 'driver')}: <b>{m.driver}</b></div>}
              {m.phone && <div>{tr('pickupPhone')}: <b>{m.phone}</b></div>}
              {m.idNote && <div>{tr('pickupId')}: <b>{m.idNote}</b></div>}
            </div>
          </div>

          <div className="doc-bl-tw">
          <table className="doc-table doc-bl-table">
            <thead><tr>
              {cells(vis, { item: <th>{tr('item')}</th>, qty: <th className="num">{tr(copy ? 'qtyDelivered' : 'qtyDispatched')}</th>, unit: <th>{tr('unit')}</th>,
                unit_price: <th className="num">{tr('unitPrice')}</th>, amount: <th className="num">{tr('amount')}</th>, received: <th className="num">{tr('qtyReceived')}</th> })}
            </tr></thead>
            <tbody>
              {m.rows.map((r, i) => (
                <tr key={i}>
                  {cells(vis, { item: <td>{r.item}</td>, qty: <td className="num">{formatQty(r.qty)}</td>,
                    unit: <td>{r.unit}{r.baseUnit && <span className="doc-bl-hint"> ({formatQty(r.baseQty)} {r.baseUnit})</span>}</td>,
                    unit_price: <td className="num">{r.price == null ? '—' : formatMoneyPrecise(r.price)}</td>, amount: <td className="num">{money(r.amount)}</td>,
                    received: <td className="num">{m.showReceived ? formatQty(r.received) : <span className="doc-bl-blank" />}</td> })}
                </tr>
              ))}
              {m.rows.length === 0 && <tr><td colSpan={cols} style={{ color: '#94a3b8' }}>{tr('empty')}</td></tr>}
            </tbody>
            <tfoot>
              {m.totals.byUnit.map((t, i) => (
                <tr key={i} className="doc-bl-total">
                  {cells(vis, { item: <td>{i === 0 ? tr('total') : ''}</td>, qty: <td className="num">{formatQty(t.qty)}</td>, unit: <td>{t.unit}</td>,
                    unit_price: <td />, amount: <td className="num">{i === 0 ? money(m.totals.amount) : ''}</td>,
                    received: <td className="num">{m.showReceived ? formatQty(t.received) : ''}</td> })}
                </tr>
              ))}
            </tfoot>
          </table>
          </div>

          {m.signatures && tplSection(tpl, 'signature') && (
            <div className="doc-bl-foot">
              <p className="doc-bl-ok">{tr('receivedGood')} <span className="doc-bl-line" /></p>
              <div className="doc-bl-signs">
                <Sign tr={tr} title={tr('sign.preparedBy')} />
                <Sign tr={tr} title={tr(m.pickup ? 'sign.collector' : 'sign.driver')} name={m.driver} />
                <Sign tr={tr} title={tr('sign.receivedBy')} />
              </div>
            </div>
          )}
          <p className="doc-note">
            <b>{tr(copy ? 'note.copy' : 'note.slip')}</b>{copy ? '' : ` ${tr('note.slipBody')}`}
            {m.number && !copy && <><br />{tr('note.reprint', { no: m.number })}</>}
          </p>
          <TplEnd tpl={tpl} />
        </div>
      )}
    </DocumentSheet>
  );
}
